import type { UISchemaElement } from '@jsonforms/core';

export interface PanelDescriptor {
  title: string;
  titleKey?: string;
  collapsible?: boolean;
  /** When 'tabs', groups within this panel render as MUI tabs instead of stacked */
  groupLayout?: 'stacked' | 'tabs';
  /** Per-group UISchemas when groupLayout is 'tabs' */
  groupUiSchemas?: Array<{ title: string; titleKey: string; uiSchema: UISchemaElement }>;
  uiSchema: UISchemaElement;
}

export interface PageTabContentItem {
  type: 'panel' | 'childGrid' | 'extension';
  panelIndex?: number;
  childGrid?: string;
  extension?: string;
}

export interface PageTabDescriptor {
  name: string;
  nameKey: string;
  default: boolean;
  panels: PanelDescriptor[];
  content: PageTabContentItem[];
}

/**
 * Recursively collect all Control scopes from a UISchema element tree.
 */
function collectScopes(elements: any[], scopes: Set<string>): void {
  for (const el of elements) {
    if (el.type === 'Control' && el.scope) {
      scopes.add(el.scope);
    }
    if (el.elements) {
      collectScopes(el.elements, scopes);
    }
  }
}

/**
 * Split an enhanced (UDF-injected) UISchema into panels by matching
 * Group labels from the panel descriptors against the enhanced elements.
 *
 * After useEnhancedJsonForms injects UDF controls into the full single UISchema,
 * this function re-splits the enhanced result into per-panel UISchemas so each
 * panel renders the enhanced version of its sections.
 */
export function splitIntoEnhancedPanels(
  enhancedUiSchema: UISchemaElement,
  panelDescriptors: PanelDescriptor[]
): PanelDescriptor[] {
  const enhanced = enhancedUiSchema as any;
  const enhancedElements: any[] = enhanced.elements || [];

  // Build map: Group label -> enhanced elements (array to handle duplicate labels across tabs)
  const elementsByLabel = new Map<string, any[]>();
  const unlabeledElements: any[] = [];

  for (const el of enhancedElements) {
    if (el.type === 'Group' && el.label) {
      const arr = elementsByLabel.get(el.label) || [];
      arr.push(el);
      elementsByLabel.set(el.label, arr);
    } else {
      unlabeledElements.push(el);
    }
  }

  // Collect control scopes from non-first panels so we can identify which
  // unlabeled elements are already claimed by another panel (e.g. empty-labeled
  // groups from Shipping/Additional panels).
  const nonFirstPanelScopes = new Set<string>();
  for (let i = 1; i < panelDescriptors.length; i++) {
    const panelElements = (panelDescriptors[i].uiSchema as any).elements || [];
    collectScopes(panelElements, nonFirstPanelScopes);
  }

  // Filter unlabeled elements: only keep those whose scopes don't overlap
  // with non-first panels (truly unclaimed fields like confirmationFailureReason)
  const unclaimedElements = unlabeledElements.filter(el => {
    const elScopes = new Set<string>();
    collectScopes([el], elScopes);
    if (elScopes.size === 0) return true; // No scopes = keep (e.g. spacers)
    return ![...elScopes].some(s => nonFirstPanelScopes.has(s));
  });

  return panelDescriptors.map((panel, index) => {
    const baseElements: any[] = (panel.uiSchema as any).elements || [];

    // Replace each element with its enhanced version (if exists)
    const enhancedPanelElements = baseElements.map((el: any) => {
      if (el.type === 'Group' && el.label && elementsByLabel.has(el.label)) {
        const candidates = elementsByLabel.get(el.label)!;
        if (candidates.length === 1) return candidates[0];
        // Multiple groups share the same label (e.g. "Contract" in Lines vs Header tab).
        // Pick the candidate whose scopes best match the base element using Jaccard similarity.
        const baseScopes = new Set<string>();
        collectScopes([el], baseScopes);
        let best = candidates[0];
        let bestScore = -Infinity;
        for (const cand of candidates) {
          const candScopes = new Set<string>();
          collectScopes([cand], candScopes);
          const intersection = [...baseScopes].filter(s => candScopes.has(s)).length;
          const union = new Set([...baseScopes, ...candScopes]).size;
          const score = union > 0 ? intersection / union : 0;
          if (score > bestScore) {
            bestScore = score;
            best = cand;
          }
        }
        return best;
      }
      return el;
    });

    // Append unclaimed ungrouped fields (e.g. confirmationFailureReason) to the first panel
    const isFirstPanel = index === 0;
    const finalElements = isFirstPanel
      ? [...enhancedPanelElements, ...unclaimedElements]
      : enhancedPanelElements;

    return {
      title: panel.title,
      ...(panel.titleKey ? { titleKey: panel.titleKey } : {}),
      ...(panel.collapsible ? { collapsible: true } : {}),
      ...(panel.groupLayout ? { groupLayout: panel.groupLayout } : {}),
      ...(panel.groupUiSchemas ? { groupUiSchemas: panel.groupUiSchemas } : {}),
      uiSchema: {
        ...enhanced,
        elements: finalElements,
      },
    };
  });
}

/**
 * Split enhanced UISchema into page tab descriptors.
 * Filters the enhanced UISchema to only include elements relevant to each tab
 * before running splitIntoEnhancedPanels, so fields from other tabs don't leak in.
 */
export function splitIntoEnhancedPageTabs(
  enhancedUiSchema: UISchemaElement,
  pageTabDescriptors: PageTabDescriptor[]
): PageTabDescriptor[] {
  const enhanced = enhancedUiSchema as any;
  const enhancedElements: any[] = enhanced.elements || [];

  // Collect group labels and scopes per tab from panel descriptors
  const tabInfo = pageTabDescriptors.map(tab => {
    const labels = new Set<string>();
    const scopes = new Set<string>();
    for (const panel of tab.panels) {
      const panelElements = (panel.uiSchema as any).elements || [];
      for (const el of panelElements) {
        if (el.type === 'Group' && el.label) labels.add(el.label);
      }
      collectScopes(panelElements, scopes);
    }
    return { labels, scopes };
  });

  return pageTabDescriptors.map((tab, tabIdx) => {
    const { labels, scopes } = tabInfo[tabIdx];

    // Filter enhanced elements to only those belonging to this tab
    const tabElements = enhancedElements.filter((el: any) => {
      // Labeled groups: match by label
      if (el.type === 'Group' && el.label) {
        return labels.has(el.label);
      }
      // Unlabeled elements: match by scope overlap
      const elScopes = new Set<string>();
      collectScopes([el], elScopes);
      if (elScopes.size === 0) return false;
      return [...elScopes].some(s => scopes.has(s));
    });

    const tabUiSchema = { ...enhanced, elements: tabElements };
    return {
      ...tab,
      panels: splitIntoEnhancedPanels(tabUiSchema, tab.panels),
    };
  });
}
